import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/* Org-level settings (OrgSettings row) via the generic /settings GET/PUT.
 * Only the fields the UI touches are typed here; the row has more. */

export interface OrgSettings {
  org_id: string;
  /** Org-wide default for "send from the deal/job owner's mailbox" on trigger
   *  campaigns. Per-campaign Campaign.send_from_owner overrides this. */
  send_from_owner_default: boolean;
  /** Earliest send time, "HH:MM" (24h). Null = no lower bound configured. */
  send_window_start: string | null;
  /** Latest send time, "HH:MM" (24h). */
  send_window_end: string | null;
  /** Day codes campaigns may send on, e.g. ["MON","TUE",...]. */
  send_days: string[];
  /** Mirror this org's Engage activity back to JobTetris as profile notes. */
  jt_activity_push_enabled: boolean;
  /** Create JT contacts for new contact-form submitters + save submission PDFs to Documents. */
  jt_forms_contact_sync_enabled: boolean;
  /** Copy candidates' details-form answers onto their JobTetris profile (JT1-1870). */
  jt_forms_writeback_enabled: boolean;
  /** Active SMS provider — only one sends at a time. Set via useSetSmsProvider. */
  active_sms_provider: 'TWILIO' | 'DIALPAD';
  /** Org-default Dialpad number used as the fallback sender when a campaign
   *  owner has no linked Dialpad number. */
  default_sms_number_id: string | null;
  /** JobTetris tenant type, recorded at sign-in: 'recruiter' (agency) or
   *  'client' (employer). Null until someone signs in after JT1-1855. */
  jt_org_type?: string | null;
  /** End every campaign text with "Reply STOP to opt out". On unless turned off (JT1-1857). */
  sms_opt_out_footer?: boolean;
  /** When campaign texts may go out, by each recipient's clock (JT1-1858). */
  sms_window_start?: string;
  sms_window_end?: string;
  sms_send_days?: string[];
  /** Most texts each recruiter may send a day; null = no per-recruiter cap. */
  sms_daily_cap_per_recruiter?: number | null;
  [key: string]: unknown;
}

export function useOrgSettings() {
  return useQuery<{ data: OrgSettings | null }>({
    queryKey: ['org-settings'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: OrgSettings | null }>('/settings');
      return data;
    },
  });
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<OrgSettings>) => {
      const { data } = await apiClient.put<{ data: OrgSettings }>('/settings', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-settings'] }),
  });
}

/**
 * Set the org's active SMS provider (only one sends at a time) and, for
 * Dialpad, the org-default fallback number. Admin-only server-side (dedicated
 * endpoint, separate from the generic settings PUT which cannot touch these).
 */
export function useSetSmsProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { active_sms_provider: 'TWILIO' | 'DIALPAD'; default_sms_number_id?: string | null }) => {
      const { data } = await apiClient.patch<{ data: OrgSettings }>('/settings/sms-provider', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-settings'] }),
  });
}
